import React, { useContext, useEffect, useState } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import nl2br from 'react-nl2br'
import dayjs from 'dayjs'
import styled, { keyframes } from 'styled-components'
import { graphql, ReplaceComponentRendererArgs } from 'gatsby'

import Angle from '../atoms/Angle'
import ECLinkList from '../organisms/ECLinkList'
import Heading from '../atoms/Heading'
import Image from '../atoms/Image'
import ItemizedReviewList from '../organisms/ItemizedReviewList'
import Layout from '../organisms/Layout'
import OuterLink from '../atoms/OuterLink'
import ProductImageList from '../organisms/ProductImageList'
import ProductList from '../organisms/ProductList'
import RecommendList from '../organisms/RecommendList'
import ReviewList from '../organisms/ReviewList'
import SEO from '../molecules/SEO'
import SurveyAnswerContext from '../contexts/SurveyAnswerContext'
import useMenu from '../hooks/useMenu'
import { CategoryListItem } from '../organisms/CategoryList'
import { SidebarRight } from '../organisms/Sidebar'
import { MysqlCategories, MysqlProducts, MysqlProductsEdge, MysqlUsers, Query } from '../graphqlTypes'

/**
 * descriptionの初期表示行数
 */
const DESCRIPTION_DEFAULT_LINE = 4

/**
 * 商品説明のID
 */
const PRODUCT_DESCRIPTION_ID = 'Product__Description'

const Container = styled.div`
  margin: 20px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: flex-start;
    display: flex;
    margin: 40px 0 0;
  }
`

const Article = styled.article`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    flex: 1;
    margin: 0 60px 0 0;
  }
`

const PageHeading = styled.div`
  align-items: center;
  display: flex;
  margin: 0 16px;
`

const PageHeadingLeft = styled.div`
  width: 70px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 85px;
  }
`

const EmblemImage = styled.img`
  width: 100%;
`

const PageHeadingRight = styled.div`
  margin: 0 0 0 12px;
  font-weight: bold;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 0 0 15px;
  }
`

const ProductImageListContainer = styled.div`
  margin: 12px 16px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0;
  }
`

const RecommendListContainer = styled.div`
  margin: 20px 16px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0;
  }
`

const Manufacturer = styled.p`
  color: ${({ theme }) => theme.palette.font.sub};
  font-size: 10px;
  margin: 0 0 4px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 16px;
  }
`

const ProductName = styled.h1`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 14px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 24px;
  }
`

const Product = styled.div`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: start;
    display: flex;
    margin: 16px 0 0;
  }
`

const ProductDetail = styled.div`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 0 0 30px;
  }
`

const Label = styled.label`
  color: ${({ theme }) => theme.palette.font.dark};
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 14px;
  }
`

const ItemizedReviewListContainer = styled.div`
  margin: 24px 16px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 38px 0 0;
  }
`

const PriceContainer = styled.div`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.palette.border.main};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.main};
  display: flex;
  justify-content: flex-end;
  margin: 12px 16px 0;
  padding: 16px 0;
  text-align: right;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 12px 0 0;
  }
`

const PriceText = styled.span`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 12px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 14px;
  }
`

const DesktopAddition = styled.p`
  color: ${({ theme }) => theme.palette.font.gray};
  font-size: 12px;
  margin: 10px 0 0;
  text-align: right;
  @media (max-width: ${({ theme }) => theme.mediaQuery.mobile.maxWidth}) {
    display: none;
  }
`

const DescriptionContainer = styled.div`
  margin: 16px 16px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 52px 0 0;
  }
`

const DescriptionText = styled.p<{ line: number; showAll: boolean }>`
  color: ${({ theme }) => theme.palette.font.main};
  display: -webkit-box;
  font-size: 12px;
  margin: 12px 0 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ line, showAll }) => (showAll ? 'unset' : line)};
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 14px;
    margin: 16px 0 0;
    -webkit-line-clamp: unset;
  }
`

const DescriptionAdditionContainer = styled.span`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const DescriptionAddition = styled.span`
  color: ${({ theme }) => theme.palette.font.gray};
  display: inline-block;
  font-size: 12px;
  margin: 16px 0 0;
`

const DescriptionReadmore = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.palette.font.link};
  display: flex;
  font-size: 12px;
  font-weight: 600;
  justify-content: center;
  margin: 16px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const rotateBottomAngle = keyframes`
  0% { transform: rotate(180deg); }
  100% { transform: rotate(360deg); }
`

const BottomAngle = styled(Angle)<{ rotate?: boolean }>`
  animation: ${rotateBottomAngle} ${({ rotate }) => (rotate ? '0.6s' : '0s')};
  object-fit: contain;
  width: 24px;
  height: 24px;
`

const ReviewHeader = styled.div`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.palette.border.light};
  box-sizing: border-box;
  color: ${({ theme }) => theme.palette.font.main};
  display: flex;
  font-size: 12px;
  font-weight: bold;
  justify-content: space-between;
  margin: 44px 0 0;
  padding: 16px;
  position: relative;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    border: 0;
    display: block;
    font-size: 14px;
    margin: 46px 0 0;
    padding: 0;
  }
`

const ReviewHeading = styled.h2`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
`

const ReviewFilterTab = styled.ul`
  align-items: center;
  display: flex;
  flex-wrap: none;
  list-style: none;
  margin: 16px 0 0;
  @media (max-width: ${({ theme }) => theme.mediaQuery.mobile.maxWidth}) {
    display: none;
  }
`

const ReviewFilterTabItem = styled.li<{ active: boolean }>`
  border-bottom: ${({ active, theme }) => (active ? `3px solid ${theme.palette.primary.light}` : 0)};
  border-left: 1px solid ${({ theme }) => theme.palette.border.main};
  box-sizing: border-box;
  color: ${({ active, theme }) => (active ? theme.palette.primary.main : theme.palette.font.link)};
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  line-height: 44px;
  width 100%;
  height: 44px;
  text-align: center;
  &:first-child {
    border-left: 0;
  }
`

const ReviewFilter = styled.div`
  align-items: center;
  color: inherit;
  display: flex;
  font-size: inherit;
  font-weight: inherit;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const toggleReviewFilterList = keyframes`
  0% { max-height: 0; }
  100% { max-height: 300px; }
`

const ReviewFilterList = styled.ul<{ open: boolean }>`
  animation: ${toggleReviewFilterList} 0.6s ease-out;
  background: ${({ theme }) => theme.palette.background.main};
  border-radius: 3px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.24), 0px 1px 4px rgba(0, 0, 0, 0.16);
  box-sizing: border-box;
  display: ${({ open }) => (open ? 'block' : 'none')};
  list-style: none;
  min-width: 260px;
  max-height: 300px;
  overflow-y: scroll;
  padding: 13px 0 0 10px;
  position: absolute;
  top: 100%;
  right: 0;
`

const ReviewFilterListItem = styled.li<{ bordered?: boolean }>`
  align-items: center;
  border-bottom: ${({ bordered, theme }) => (bordered ? `1px solid ${theme.palette.border.light}` : 0)};
  display: flex;
  font-weight: normal;
  justify-content: space-between;
  min-height: 44px;
  &:first-child {
    border: 0;
    font-weight: bold;
    min-height: unset;
    padding: 0 0 10px;
  }
  &:last-child {
    border: 0;
  }
`

const ReviewFilterListItemText = styled.span`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 12px;
`

const CheckIcon = styled(Image)`
  margin: 0 10px 0 0;
  object-fit: contain;
  width: 18px;
  height: 18px;
`

const ReviewListContainer = styled.div`
  background: ${({ theme }) => theme.palette.primary.pale};
  padding: 16px 12px 32px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    padding: 20px 20px 30px;
  }
`

const SocialShare = styled.aside`
  margin: 32px 20px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 20px 20px 0;
  }
`

const SocialShareBalloon = styled.div`
  margin: 0 auto;
  width: 250px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0;
    width: 170px;
  }
`

const SocialShareButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 0 0 26px;
  }
`

const SocialShareButton = styled(OuterLink)`
  display: block;
  margin: 0 0 0 15px;
  text-decoration: none;
  width: 160px;
  &:first-child {
    margin: 0;
  }
`

const RelationalProduct = styled.div`
  margin: 32px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 75px 0 0;
  }
`

const RelationalHeading = styled(Heading)`
  && {
    @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
      align-items: center;
      background: transparent;
      border-left: 5px solid ${({ theme }) => theme.palette.primary.main};
      color: ${({ theme }) => theme.palette.font.main};
      display: flex;
      font-size: 18px;
      justify-content: start;
      padding: 0 0 0 15px;
    }
  }
`

const RelationalCategory = styled.div`
  margin: 32px 0 -14px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const RelationalCategoryList = styled.ul`
  list-style: none;
`

const RelationalCategoryListItem = styled(CategoryListItem)`
  &:last-child {
    border-width: 1px;
  }
`

type Props = ReplaceComponentRendererArgs['props'] & {
  pageContext: { product: MysqlProducts; relationalProducts: MysqlProductsEdge[] }
} & { data: Query }

export default function ProductPage(props: Props) {
  const { reviewFilters } = useContext(SurveyAnswerContext)
  const [showAllDescription, setShowAllDescription] = useState(false)
  const [openReviewFilter, setOpenReviewFilter] = useState(false)
  const [filteredValue, setFilteredValue] = useState<string | null>(null)

  useEffect(() => {
    const element = document.querySelector(`#${PRODUCT_DESCRIPTION_ID}`) as HTMLElement
    setShowAllDescription(element.offsetHeight >= element.scrollHeight)
  }, [])

  const { allMysqlCategories, allMysqlRanks, allMysqlSurveyQuestions, allMysqlSurveyAnswers, site } = props.data
  const { product, relationalProducts } = props.pageContext

  const parentCategory = allMysqlCategories.edges.find(({ node }) => {
    const children = node.categories as MysqlCategories[]
    return children.map((child) => child.mysqlId).includes(product.category_id)
  })
  if (!parentCategory) throw new TypeError('"parentCategory" is undefined')

  const children = parentCategory.node.categories as MysqlCategories[]

  const surveyAnswers = allMysqlSurveyAnswers.edges.filter(
    ({ node }) => product.mysqlId === node.product_id && node.users != null
  )

  // 絞り込み項目が存在すればセットする
  const reviewFilter = reviewFilters.find(({ categories }) => categories.includes(product.category_id!))
  const reviewFilterValues = reviewFilter
    ? [
        ...new Set(
          surveyAnswers
            .map<string>(({ node }) => {
              const user = node.users as MysqlUsers
              const value = user[reviewFilter.column]

              if (reviewFilter.column === 'mother_birthday') {
                const age = dayjs(user.created).diff(dayjs(user.mother_birthday), 'year')
                if (age < 20) return '10代後半'
                if (age < 25) return '20代前半'
                if (age < 30) return '20代後半'
                if (age < 35) return '30代前半'
                if (age < 40) return '30代後半'
                if (age < 45) return '40代前半'
                if (age < 50) return '40代後半'
                return ''
              }

              return typeof value === 'string' ? value : ''
            })
            .filter((value) => value)
            .sort()
        ),
      ]
    : []

  const menu = useMenu(allMysqlCategories.edges, allMysqlRanks.group)

  return (
    <Layout menu={menu}>
      <SEO
        description={`${product.name} を使ってみた実際の感想、口コミ、評判などを項目別に満足度を取ってまとめています。良かった点（メリット）や合わなかった点（デメリット）、スペック、価格、機能、比較などの面で書かれたレビューをご紹介します`}
        image={`${process.env.GATSBY_CDN_URL}/${process.env.GATSBY_CDN_PATH}/ogp/top.png`}
        title={`${product.name} 口コミ | ${site!.siteMetadata!.title}`}
        type="article"
        url={'https://award.mamari.jp' + product.fields!.pagePath!}
      />
      <Container>
        <Article>
          <PageHeading>
            <PageHeadingLeft>
              <LazyLoadComponent>
                <EmblemImage alt={`${product.name} エンブレム`} src={`${product.fields!.emblemImageUrl}?auto=format`} />
              </LazyLoadComponent>
            </PageHeadingLeft>
            <PageHeadingRight>
              <Manufacturer>{product.manufacturer}</Manufacturer>
              <ProductName>{product.name}</ProductName>
            </PageHeadingRight>
          </PageHeading>

          <Product>
            <ProductImageListContainer>
              <ProductImageList product={product} />
            </ProductImageListContainer>

            <ProductDetail>
              <RecommendListContainer>
                <RecommendList product={product} variantHeading="h2" />
              </RecommendListContainer>

              <ItemizedReviewListContainer>
                <Label>項目別評価</Label>
                <ItemizedReviewList
                  product={product}
                  ranks={allMysqlRanks.edges}
                  surveyQuestions={allMysqlSurveyQuestions.edges}
                />
              </ItemizedReviewListContainer>

              <PriceContainer>
                <PriceText>{product.display_price}</PriceText>
              </PriceContainer>
              <DesktopAddition>{nl2br(product.addition!)}</DesktopAddition>
            </ProductDetail>
          </Product>

          <DescriptionContainer>
            <Label>商品概要</Label>
            <DescriptionText id={PRODUCT_DESCRIPTION_ID} line={DESCRIPTION_DEFAULT_LINE} showAll={showAllDescription}>
              {nl2br(product.description!)}
              {product.addition != null && product.addition.length > 0 && (
                <DescriptionAdditionContainer>
                  <br />
                  <DescriptionAddition>{nl2br(product.addition!)}</DescriptionAddition>
                </DescriptionAdditionContainer>
              )}
            </DescriptionText>
            {!showAllDescription && (
              <DescriptionReadmore onClick={() => setShowAllDescription(true)}>
                続きを読む
                <BottomAngle color="pink" direction="bottom" />
              </DescriptionReadmore>
            )}
          </DescriptionContainer>

          <ECLinkList officialUrl={product.official_url!} ecUrls={product.ec_urls ? JSON.parse(product.ec_urls) : []} />
          <ReviewHeader>
            <ReviewHeading>ママたちの口コミ</ReviewHeading>
            {reviewFilterValues.length > 0 && (
              <React.Fragment>
                {/* Desktop */}
                <ReviewFilterTab>
                  <ReviewFilterTabItem
                    active={filteredValue == null}
                    onClick={() => {
                      setFilteredValue(null)
                    }}
                  >
                    すべて
                  </ReviewFilterTabItem>
                  {reviewFilterValues.map((value, index) => (
                    <ReviewFilterTabItem
                      active={filteredValue === value}
                      key={index}
                      onClick={() => {
                        setFilteredValue(filteredValue === value ? null : value)
                      }}
                    >
                      {value}
                    </ReviewFilterTabItem>
                  ))}
                </ReviewFilterTab>

                {/* Mobile */}
                <ReviewFilter onClick={() => setOpenReviewFilter(!openReviewFilter)}>
                  絞り込み
                  <BottomAngle rotate color="pink" direction={openReviewFilter ? 'top' : 'bottom'} />
                </ReviewFilter>
                <ReviewFilterList open={openReviewFilter}>
                  <ReviewFilterListItem>カテゴリ</ReviewFilterListItem>
                  <ReviewFilterListItem
                    bordered
                    onClick={() => {
                      setFilteredValue(null)
                      setOpenReviewFilter(false)
                    }}
                  >
                    <ReviewFilterListItemText>すべて</ReviewFilterListItemText>
                    {!filteredValue && <CheckIcon alt="「すべて」を選択済み" filename="icon-check.png" />}
                  </ReviewFilterListItem>
                  {reviewFilterValues.map((value, index) => (
                    <ReviewFilterListItem
                      bordered
                      key={index}
                      onClick={() => {
                        setFilteredValue(filteredValue === value ? null : value)
                        setOpenReviewFilter(false)
                      }}
                    >
                      <ReviewFilterListItemText>{value}</ReviewFilterListItemText>
                      {filteredValue === value && (
                        <CheckIcon alt={`「${value}」を選択済み`} filename="icon-check.png" />
                      )}
                    </ReviewFilterListItem>
                  ))}
                </ReviewFilterList>
              </React.Fragment>
            )}
          </ReviewHeader>
          <ReviewListContainer>
            <ReviewList
              category={product.category_id!}
              surveyAnswers={surveyAnswers.filter(({ node }) => {
                if (!reviewFilter || !filteredValue) return true

                const user = node.users as MysqlUsers
                if (reviewFilter.column === 'mother_birthday') {
                  const age = dayjs(user.created).diff(dayjs(user.mother_birthday), 'year')
                  if (filteredValue === '10代後半') return age < 20
                  if (filteredValue === '20代前半') return 20 <= age && age < 25
                  if (filteredValue === '20代後半') return 25 <= age && age < 30
                  if (filteredValue === '30代前半') return 30 <= age && age < 35
                  if (filteredValue === '30代後半') return 35 <= age && age < 40
                  if (filteredValue === '40代前半') return 40 <= age && age < 45
                  if (filteredValue === '40代後半') return 45 <= age && age < 50
                  return true
                }

                return filteredValue === user[reviewFilter.column]
              })}
            />
          </ReviewListContainer>

          <SocialShare>
            <SocialShareBalloon>
              <Image alt="パパや友達にオススメしてね！" filename="balloon-share.png" />
            </SocialShareBalloon>
            <SocialShareButtonGroup>
              <SocialShareButton
                to={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(
                  `https://award.mamari.jp/products/${product.mysqlId}`
                )}`}
              >
                <Image filename="share-line.png" />
              </SocialShareButton>
              <SocialShareButton
                to={`https://twitter.com/share?url=${encodeURIComponent(
                  `https://award.mamari.jp/products/${product.mysqlId}`
                )}&text=${encodeURIComponent(
                  `${product.name} が、先輩ママの「本当に使ってよかった」商品・サービスに選ばれました！ #ママリ口コミ大賞 2020秋→`
                )}`}
              >
                <Image filename="share-twitter.png" />
              </SocialShareButton>
            </SocialShareButtonGroup>
          </SocialShare>

          <RelationalProduct>
            <RelationalHeading>同じ部門の受賞商品</RelationalHeading>
            <ProductList products={relationalProducts} />
          </RelationalProduct>

          {children.length > 1 && (
            <RelationalCategory>
              <Heading>同じカテゴリの部門一覧</Heading>
              <RelationalCategoryList>
                {children
                  .filter((child: MysqlCategories) => product.category_id !== child.mysqlId)
                  .map((child: MysqlCategories) => (
                    <RelationalCategoryListItem large category={child} to={child.fields!.pagePath!} key={child.id} />
                  ))}
              </RelationalCategoryList>
            </RelationalCategory>
          )}
        </Article>
        <SidebarRight />
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query($awardId: Int) {
    allMysqlCategories(filter: { parent_id: { eq: null } }) {
      edges {
        node {
          id
          mysqlId
          slug
          title
          categories {
            id
            mysqlId
            slug
            title
            fields {
              imageUrl
              pagePath
            }
          }
        }
      }
    }
    allMysqlRanks(filter: { award_id: { eq: $awardId } }) {
      edges {
        node {
          id
          category_id
          product_id
          rank
          ratings
          total_rating
        }
      }
      group(field: category_id) {
        fieldValue
        edges {
          node {
            id
            mysqlId
            category_id
            rank
            products {
              id
              mysqlId
              category_id
              fields {
                imageUrl
              }
            }
          }
        }
      }
    }
    allMysqlSurveyQuestions(filter: { award_id: { eq: $awardId } }) {
      edges {
        node {
          id
          mysqlId
          content
        }
      }
    }
    allMysqlSurveyAnswers(
      filter: { award_id: { eq: $awardId }, is_sensitive: { eq: 0 } }
      sort: { fields: [display_position], order: ASC }
    ) {
      edges {
        node {
          id
          product_id
          ratings
          comment
          using_time
          users {
            prefecture_id
            residential_area
            building_type
            floor_plan
            noise_care
            transport
            car_model
            step_existence
            household_income
            support_member
            mother_job
            mother_birthday
            mother_height
            mother_weight
            milk
            youngest_child_birthday
            youngest_child_sex
            youngest_child_height
            youngest_child_weight
            other_children
            created
          }
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`
